import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { Flex, Text } from '@mantine/core'
import { type Dispatch, memo, type SetStateAction, useEffect } from 'react'

import { useTranslation } from '@flecto/translations'

import { Icon } from '../../../../Icon/Icon'
import { useDateRange } from '../../../hooks/useDateRange'
import { type FilterValue } from '../../multi-select/filterTypeConfig'
import { DateTimeRangePicker } from '../DateTimeRangePicker'

type DateTimeRangeFilterWrapperProps = {
  selectedFilters: FilterValue[]
  setSelectedFilters: Dispatch<SetStateAction<FilterValue[]>>
  onInvalidRangeChange: (isInvalid: boolean) => void
}

export const DateTimeRangeFilterWrapper = memo(
  ({
    selectedFilters,
    setSelectedFilters,
    onInvalidRangeChange,
  }: Readonly<DateTimeRangeFilterWrapperProps>) => {
    const { t } = useTranslation('shared', { keyPrefix: 'filters' })
    const { handleDateChange, handleTimeChange, isInvalidRange, getDateRange } =
      useDateRange(selectedFilters, setSelectedFilters)
    const dateRange = getDateRange()

    useEffect(() => {
      onInvalidRangeChange(isInvalidRange)
    }, [isInvalidRange, onInvalidRangeChange])

    return (
      <>
        <Text size='xs' mb='xs' c='gray.6'>
          {t('dateRange.between')}
        </Text>
        <Flex direction='column' gap='md'>
          <Flex data-testid='data-range-start-date-time-container'>
            <DateTimeRangePicker
              dateValue={
                dateRange.startDate ? new Date(dateRange.startDate) : null
              }
              error={isInvalidRange}
              timeValue={dateRange.startTime}
              onDateChange={(date) => handleDateChange('startDate', date)}
              onTimeChange={(event) =>
                handleTimeChange('startTime', event.target.value)
              }
            />
          </Flex>
          <Flex data-testid='data-range-end-date-time-container'>
            <DateTimeRangePicker
              dateValue={dateRange.endDate ? new Date(dateRange.endDate) : null}
              timeValue={dateRange.endTime}
              onDateChange={(date) => handleDateChange('endDate', date)}
              error={isInvalidRange}
              onTimeChange={(event) =>
                handleTimeChange('endTime', event.target.value)
              }
            />
          </Flex>
        </Flex>
        {isInvalidRange && (
          <Flex gap='xs' mt='sm' align='center'>
            <Icon icon={faCircleExclamation} color='red.6' />
            <Text size='xs' c='red.6' data-testid='date-range-error-message'>
              {t('dateRange.error.invalidRange')}
            </Text>
          </Flex>
        )}
      </>
    )
  }
)

DateTimeRangeFilterWrapper.displayName = 'DateTimeRangeFilterWrapper'
